import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "cu_cart_v1";
const COUPON_KEY = "cu_coupon_v1";

const loadCart = () => {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const loadCoupon = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(COUPON_KEY)) || null;
  } catch {
    return null;
  }
};

const makeKey = (item) =>
  `${item.product_id}__${item.size || "_"}__${item.color || "_"}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);
  const [coupon, setCouponState] = useState(loadCoupon);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    else localStorage.removeItem(COUPON_KEY);
  }, [coupon]);

  const setCoupon = (c) => setCouponState(c);
  const clearCoupon = () => setCouponState(null);

  const addItem = (newItem) => {
    const key = makeKey(newItem);
    setItems((prev) => {
      const found = prev.find((i) => makeKey(i) === key);
      if (found) {
        return prev.map((i) =>
          makeKey(i) === key ? { ...i, quantity: i.quantity + newItem.quantity } : i,
        );
      }
      return [...prev, newItem];
    });
    setIsOpen(true);
  };

  const removeItem = (key) =>
    setItems((prev) => prev.filter((i) => makeKey(i) !== key));

  const updateQuantity = (key, qty) =>
    setItems((prev) =>
      prev.map((i) =>
        makeKey(i) === key ? { ...i, quantity: Math.max(1, qty) } : i,
      ),
    );

  const clear = () => {
    setItems([]);
    setCouponState(null);
  };

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        setIsOpen,
        addItem,
        removeItem,
        updateQuantity,
        clear,
        count,
        subtotal,
        makeKey,
        coupon,
        setCoupon,
        clearCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
